import { DatePicker } from 'antd';
import '../VtxDatePicker.scss';
import React, { FunctionComponent } from 'react';
import classNames from 'classnames';
import { DatePickerProps } from 'antd/lib/date-picker';
import { Instant } from '@js-joda/core';
import moment from 'moment';
import { InstantToMoment, momentToInstant } from '../dateHelperFunctions';
import { IAutomatable, preventDataAutomationOverriding } from '../../../helpers/IAutomatable';

export interface VtxDateTimePickerProps
    extends Omit<
            DatePickerProps,
            | 'picker'
            | 'showTime'
            | 'onChange'
            | 'dateRender'
            | 'monthCellRender'
            | 'onOk'
            | 'onPanelChange'
            | 'onSelect'
            | 'disabledDate'
            | 'value'
            | 'format'
            | 'defaultValue'
            | 'defaultPickerValue'
            | 'format'
        >,
        IAutomatable {
    /**
     * to set default date, if start time or end time is null or undefined, the date range will be an open interval
     */
    defaultValue?: Readonly<Instant>;
    /**
     * to set default picker date
     */
    defaultPickerValue?: Readonly<Instant>;
    onChange?: (day: Instant | null) => void;
    onSelect?: (day: Instant | null) => void;
    disabledDate?: (date: Instant) => boolean;
    onOk?: (date: Instant) => void;
    /**
     * @deprecated if you want custom styling, please reach out to the UX team for approval
     */
    dateRender?: never;
    /**
     * @deprecated Use one of the named widgets instead like VtxDayPicker, VtxRangePicker, VtxYearPicker instead
     */
    picker?: never;
    /**
     * Tells the widget whether or not to show the time portion of widget. If you don't want to show this then use VtxDayPicker instead.
     */
    showTime?: true;
    value?: Instant | null;
    /**
     * @deprecated Currently panel types must remain static
     */
    onPanelChange?: never;
}

/**
 * In most cases, you probably want to use VtxDayPicker instead of this due to it's superior API. However, sometimes you want represent the time as well as the date. This allows the user to provide the time portion instead of letting the browser guess.
 */
export const VtxDateTimePicker: FunctionComponent<VtxDateTimePickerProps> = React.memo(
    (props: VtxDateTimePickerProps) => {
        const automationClass = 'vtx-date-time-picker';
        const {
            className,
            onChange,
            dateRender,
            showTime,
            onOk,
            onSelect,
            disabledDate,
            value,
            defaultValue,
            defaultPickerValue,
            ...otherProps
        } = preventDataAutomationOverriding(props);

        const defaultValueAsMoment = defaultValue ? moment(defaultValue.toString()) : undefined;
        const defaultPickerValueAsMoment = defaultPickerValue
            ? moment(defaultPickerValue.toString())
            : undefined;

        const defaultFormat = 'MM/DD/YYYY hh:mm a';

        const wrappedOnChange: DatePickerProps['onChange'] = date => {
            if (onChange && date) {
                const ZonedDateTime = momentToInstant(date);
                onChange(ZonedDateTime);
            } else if (onChange) {
                onChange(null);
            }
        };

        const wrappedOnOk: DatePickerProps['onOk'] = date => {
            if (onOk) {
                const ZonedDateTime = momentToInstant(date);
                onOk(ZonedDateTime);
            }
        };

        const wrappedOnSelect: DatePickerProps['onSelect'] = date => {
            if (onSelect) {
                const ZonedDateTime = momentToInstant(date);
                onSelect(ZonedDateTime);
            }
        };

        const wrappedDisabledDate: DatePickerProps['disabledDate'] = disabledDate
            ? date => {
                  const ZonedDateTime = momentToInstant(date);
                  return disabledDate(ZonedDateTime);
              }
            : undefined;

        const classNamesCombined = classNames(automationClass, className);
        return (
            <div data-automation-class={automationClass} className={classNamesCombined}>
                <DatePicker
                    picker="date"
                    showTime={showTime}
                    disabledDate={wrappedDisabledDate}
                    defaultValue={defaultValueAsMoment}
                    defaultPickerValue={defaultPickerValueAsMoment}
                    value={value ? InstantToMoment(value) : value}
                    onChange={wrappedOnChange}
                    onSelect={wrappedOnSelect}
                    onOk={wrappedOnOk}
                    format={defaultFormat}
                    {...otherProps}
                />
            </div>
        );
    }
);
